import React, { useState, useEffect } from "react";
import {
  Box,
  Container,
  Typography,
  Avatar,
  Grid,
  Card,
  CardContent,
  Button,
  Divider,
  Chip,
  AppBar,
  Toolbar,
  CircularProgress,
} from "@mui/material";
import {
  ArrowBack,
  Person,
  Work,
  School,
  Email,
  LocationOn,
  Business,
  CalendarToday,
  Cake,
  AccountCircle,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { apiGet } from "../api/apiClient";
import "./ProfilePage.css";

const ProfilePage = () => {
  const navigate = useNavigate();
  const [employeeData, setEmployeeData] = useState(null);
  const [otherInfo, setOtherInfo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      const employee = JSON.parse(localStorage.getItem("employee") || "{}");

      // Fetch other_info
      const allOtherInfo = await apiGet('/other_info');
      const userOtherInfo = allOtherInfo.find(
        (info) => info.employee_code === employee.employee_code
      );
      setOtherInfo(userOtherInfo);

      setEmployeeData(employee);
    } catch (error) {
      console.error("Failed to fetch employee data:", error);
    } finally {
      setLoading(false);
    }
  };

  const getInitials = (name) => {
    if (!name) return "?";
    return name
      .split(" ")
      .map((n) => n[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  const handleBackClick = () => {
    navigate("/home");
  };

  if (loading) {
    return (
      <Box className="profile-loading">
        <CircularProgress size={60} className="profile-loading-spinner" />
      </Box>
    );
  }

  if (!employeeData) {
    return (
      <Box className="profile-error">
        <Typography variant="h5" color="error">
          Employee data not found
        </Typography>
      </Box>
    );
  }

  return (
    <Box className="profile-page">
      {/* Top Navigation Bar */}
      <AppBar position="static" elevation={0} className="profile-top-bar">
        <Toolbar>
          <Button
            onClick={handleBackClick}
            startIcon={<ArrowBack />}
            className="profile-back-button"
          >
            Back to Dashboard
          </Button>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="lg" className="profile-container">
        {/* Section 1: Basic Info */}
        <Card className="profile-card">
          <CardContent className="profile-card-content">
            <Box className="profile-header profile-header-mobile profile-header-desktop">
              <Avatar
                className="profile-avatar"
                sx={{
                  width: 100,
                  height: 100,
                  backgroundColor: "#0D004A",
                  fontSize: "3.5rem",
                }}
              >
                {getInitials(employeeData.employee_name)}
              </Avatar>
              <Box className="profile-header-info profile-header-info-centered profile-header-info-left">
                <Typography variant="h4" className="profile-name">
                  {employeeData.employee_name}
                </Typography>
                <Typography variant="h6" className="profile-title">
                  {employeeData.designation_id} • {employeeData.department_name}
                </Typography>
                <Box className="profile-chips profile-chips-centered profile-chips-left">
                  <Chip
                    sx={{ 
                      backgroundColor: "white",
                      border: "2px solid #0D004A",
                      color: "#0D004A",
                      '& .MuiChip-icon': { color: 'red' }
                    }}
                    icon={<Work />}
                    label={`ID: ${employeeData.employee_code}`}
                    className="profile-chip-primary"
                  />
                  <Chip
                    sx={{ 
                      backgroundColor: "white",
                      border: "2px solid #0D004A",
                      color: "#0D004A",
                      '& .MuiChip-icon': { color: 'red' }
                    }}
                    icon={<LocationOn />}
                    label={employeeData.location_id || "N/A"}
                    className="profile-chip-primary"
                  />
                </Box>
              </Box>
            </Box>

            <Divider className="profile-divider" />

            {/* Professional Info Grid with Accent Lines */}
            <Grid container spacing={2} className="profile-info-grid">
              {/* Client */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <Business className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Client
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.client || otherInfo?.client || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              {/* Department */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <Business className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Department
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.department_name || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              {/* Designation */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <Work className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Designation
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.designation_id || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              {/* Reporting Manager */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <AccountCircle className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Reports To
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.reporting_to || "Not Assigned"}
                  </Typography>
                </Box>
              </Grid>

              {/* Email */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <Email className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Email
                    </Typography>
                  </Box>
                  <Typography
                    variant="h6"
                    className="profile-info-value profile-info-value-email"
                  >
                    {employeeData.office_email || otherInfo?.email || "N/A"}
                  </Typography>
                </Box>
              </Grid>

              {/* Work Location */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <LocationOn className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Location
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.city_id} ({employeeData.state_id})
                  </Typography>
                </Box>
              </Grid>

              {/* Date of Joining */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <CalendarToday className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Joined On
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.joining_date
                      ? new Date(employeeData.joining_date).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )
                      : otherInfo?.joining_date
                      ? new Date(otherInfo.joining_date).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )
                      : "N/A"}
                  </Typography>
                </Box>
              </Grid>

              {/* Date of Birth */}
              <Grid size={{ xs: 12, sm: 6, md: 3 }}>
                <Box className="profile-info-item">
                  <Box className="profile-info-header">
                    <Cake className="profile-info-icon" />
                    <Typography
                      variant="caption"
                      className="profile-info-label"
                    >
                      Birthday
                    </Typography>
                  </Box>
                  <Typography variant="h6" className="profile-info-value">
                    {employeeData.date_of_birth
                      ? new Date(employeeData.date_of_birth).toLocaleDateString(
                          "en-US",
                          { year: "numeric", month: "short", day: "numeric" }
                        )
                      : otherInfo?.dob
                      ? new Date(otherInfo.dob).toLocaleDateString("en-US", {
                          year: "numeric",
                          month: "short",
                          day: "numeric",
                        })
                      : "N/A"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Section 2: Project History */}
        <Card className="profile-section-card">
          <CardContent className="profile-section-content">
            <Typography variant="h5" className="profile-section-title">
              <Work /> Project History
            </Typography>
            <Box className="profile-section-placeholder">
              <Typography
                variant="body1"
                className="profile-section-placeholder-text"
              >
                Project history details will be available once integrated with
                work_history API.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Section 3: Training & Certifications */}
        <Card className="profile-section-card">
          <CardContent className="profile-section-content">
            <Typography variant="h5" className="profile-section-title">
              <School /> Training & Certifications
            </Typography>
            <Box className="profile-section-placeholder">
              <Typography
                variant="body1"
                className="profile-section-placeholder-text"
              >
                Training and certification details will be available once
                integrated with learning_history API.
              </Typography>
            </Box>
          </CardContent>
        </Card>

        {/* Section 4: Personal Touch */}
        <Card className="profile-section-card">
          <CardContent className="profile-section-content">
            <Typography variant="h5" className="profile-section-title">
              <Person /> Personal Information
            </Typography>
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box className="profile-personal-item">
                  <Typography
                    variant="body2"
                    className="profile-personal-label"
                  >
                    Father's Name
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-personal-value"
                  >
                    {otherInfo?.father_name || "Not available"}
                  </Typography>
                </Box>
                <Box className="profile-personal-item">
                  <Typography
                    variant="body2"
                    className="profile-personal-label"
                  >
                    Mother's Name
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-personal-value"
                  >
                    {otherInfo?.mother_name || "Not available"}
                  </Typography>
                </Box>
                <Box className="profile-personal-item">
                  <Typography
                    variant="body2"
                    className="profile-personal-label"
                  >
                    Marital Status
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-personal-value"
                  >
                    {otherInfo?.marital_status || "Not specified"}
                  </Typography>
                </Box>
              </Grid>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box className="profile-personal-item">
                  <Typography
                    variant="body2"
                    className="profile-personal-label-with-icon"
                  >
                    <LocationOn fontSize="small" /> Address
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-personal-value"
                  >
                    {otherInfo?.current_address || "Not available"}
                  </Typography>
                </Box>
                <Box className="profile-personal-item">
                  <Typography
                    variant="body2"
                    className="profile-personal-label"
                  >
                    Emergency Contact
                  </Typography>
                  <Typography
                    variant="body1"
                    className="profile-personal-value"
                  >
                    {otherInfo?.emergency_contact_no || "Not available"}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </CardContent>
        </Card>
      </Container>
    </Box>
  );
};

export default ProfilePage;
